import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { List } from 'immutable';
import { useSelector } from 'react-redux';
import { Paper, TextField, InputAdornment, Grid } from '@mui/material';
import { useTranslation } from 'src/i18n';
import { getTemperatureUnit } from 'src/module/authentication/selector';
import { celsiusToFahrenheitConvertor, fahrenheitToCelsiusConvertor } from 'src/utils/utils';
import CircuitDynamicTable from '../CommissioningCircuit/CircuitDynamicTable';
import YesNoNaField from '../Commissioning/YesNoNaField';

// Section 3: inspection dates, site conditions, equipment used, inspectors, client reps.
export default function HmsAuditInspection (props) {
  const { data, onChange, readOnly } = props;
  const { t } = useTranslation();
  const temperatureUnit = useSelector(getTemperatureUnit);

  const inspection = data?.get('inspection');
  const siteConditions = inspection?.get('siteConditions');

  // Holds raw user input for the temperature while editing. On blur we convert F→C and commit,
  // avoiding round-trip precision drift and preserving states like "7." or "-".
  const [tempInput, setTempInput] = useState<string | null>(null);
  useEffect(() => { setTempInput(null); }, [temperatureUnit]);

  const storedTemp = siteConditions?.get('averageTemperature') || '';
  const tempDisplayValue = () => {
    if (tempInput !== null) return tempInput;
    if (temperatureUnit === 'F') {
      const numeric = parseFloat(storedTemp);
      return isNaN(numeric) ? storedTemp : String(celsiusToFahrenheitConvertor(numeric));
    }
    return storedTemp;
  };

  const onTempBlur = () => {
    if (tempInput === null) return;
    const numeric = parseFloat(tempInput);
    const stored = temperatureUnit === 'F' && !isNaN(numeric)
      ? String(fahrenheitToCelsiusConvertor(numeric))
      : tempInput;
    onChange(['extras', 'inspection', 'siteConditions', 'averageTemperature'], stored);
    setTempInput(null);
  };

  const equipmentColumns = useMemo(() => [
    { key: 'equipment', label: t('hmsAudit.equipment.equipment') },
    { key: 'idNumber', label: t('hmsAudit.equipment.idNumber') },
    { key: 'calDueDate', label: t('hmsAudit.equipment.calDueDate'), type: 'date' },
  ], [t]);

  const personColumns = useMemo(() => [
    { key: 'name', label: t('hmsAudit.people.name') },
    { key: 'titleCompany', label: t('hmsAudit.people.titleCompany') },
  ], [t]);

  return (
    <div>
      {/* Dates and site conditions sit side by side; both are short enough that stacking them
          wasted the row. They fall back to full width below the md breakpoint. */}
      <Grid container spacing={2} alignItems='stretch' className='field-report-row'>
        <Grid item xs={12} md={5}>
          <Paper className='field-report-section full-height'>
            <h3>{t('hmsAudit.inspectionDates')}</h3>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type='date'
                  label={t('hmsAudit.dateFrom')}
                  InputLabelProps={{ shrink: true }}
                  disabled={readOnly}
                  value={inspection?.get('dateFrom') || ''}
                  onChange={(e) => onChange(['extras', 'inspection', 'dateFrom'], e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type='date'
                  label={t('hmsAudit.dateTo')}
                  InputLabelProps={{ shrink: true }}
                  disabled={readOnly}
                  value={inspection?.get('dateTo') || ''}
                  onChange={(e) => onChange(['extras', 'inspection', 'dateTo'], e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper className='field-report-section full-height'>
            <h3>{t('hmsAudit.siteConditions')}</h3>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type='number'
                  label={t('hmsAudit.averageTemperature')}
                  disabled={readOnly}
                  value={tempDisplayValue()}
                  onChange={(e) => setTempInput(e.target.value)}
                  onBlur={onTempBlur}
                  InputProps={{ endAdornment: <InputAdornment position='end'>°{temperatureUnit}</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <YesNoNaField
                  label={t('hmsAudit.sunny')}
                  value={siteConditions?.get('sunny')}
                  readOnly={readOnly}
                  showNa={false}
                  onChange={(val) => onChange(['extras', 'inspection', 'siteConditions', 'sunny'], val)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <YesNoNaField
                  label={t('hmsAudit.rain')}
                  value={siteConditions?.get('rain')}
                  readOnly={readOnly}
                  showNa={false}
                  onChange={(val) => onChange(['extras', 'inspection', 'siteConditions', 'rain'], val)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <CircuitDynamicTable
        title={t('hmsAudit.equipmentUsed')}
        columns={equipmentColumns}
        data={inspection?.get('equipmentUsed') as List<any>}
        extrasKey={['inspection', 'equipmentUsed']}
        onChange={onChange}
        readOnly={readOnly}
      />

      <CircuitDynamicTable
        title={t('hmsAudit.inspectors')}
        columns={personColumns}
        data={inspection?.get('inspectors') as List<any>}
        extrasKey={['inspection', 'inspectors']}
        onChange={onChange}
        readOnly={readOnly}
        deletable
        minRows={1}
      />

      <CircuitDynamicTable
        title={t('hmsAudit.clientRepresentatives')}
        columns={personColumns}
        data={inspection?.get('clientReps') as List<any>}
        extrasKey={['inspection', 'clientReps']}
        onChange={onChange}
        readOnly={readOnly}
        deletable
        minRows={1}
      />
    </div>
  );
}

HmsAuditInspection.propTypes = {
  data: ImmutablePropTypes.map,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

HmsAuditInspection.defaultProps = {
  readOnly: false,
};
